import React, { useState } from 'react';

/**
 * A self-contained, controlled React form used as the built-in target for the
 * Admin → Tester Lab. It intentionally mirrors the engine's jsdom fixture
 * (__fixtures__/form-app.html) but as a *real controlled React component*, so
 * replaying against it also validates the engine's React-safe value setter
 * (prototype `value` setter + dispatched input/change → onChange fires).
 *
 * Step 2 and the final confirmation are revealed asynchronously (setTimeout) so
 * replay must wait for them to appear — exercising the wait-for-target logic.
 *
 * NOTE: this element must NOT be wrapped in [data-lyric-tester-ui]; that marker
 * tells the recorder to ignore the Lab's own chrome. This is the app-under-test.
 */
export default function SampleApp() {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [subscribe, setSubscribe] = useState(false);
    const [plan, setPlan] = useState('free');
    const [step2, setStep2] = useState(false);
    const [done, setDone] = useState('');
    const [error, setError] = useState(false);

    const onContinue = () => {
        if (!fullName.trim() || !email.trim()) { setError(true); return; }
        setError(false);
        setTimeout(() => setStep2(true), 250);
    };
    const onFinish = () => {
        setTimeout(() => setDone(`All set — welcome, ${email} on the ${plan} plan!`), 250);
    };

    return (
        <form
            id="signup"
            data-testid="signup-form"
            onSubmit={(e) => e.preventDefault()}
            style={S.form}
        >
            <fieldset style={S.fieldset}>
                <legend style={S.legend}>Step 1 — Create account</legend>

                <label style={S.label}>Full name
                    <input
                        id="fullName"
                        name="fullName"
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        style={S.input}
                    />
                </label>

                <label style={S.label}>Email
                    <input
                        data-testid="email-field"
                        name="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={S.input}
                    />
                </label>

                <label style={S.checkRow}>
                    <input
                        id="subscribe"
                        name="subscribe"
                        type="checkbox"
                        checked={subscribe}
                        onChange={(e) => setSubscribe(e.target.checked)}
                    />
                    Subscribe to updates
                </label>

                <label style={S.label}>Plan
                    <select
                        id="plan"
                        name="plan"
                        value={plan}
                        onChange={(e) => setPlan(e.target.value)}
                        style={S.input}
                    >
                        <option value="free">Free</option>
                        <option value="pro">Pro</option>
                        <option value="enterprise">Enterprise</option>
                    </select>
                </label>

                <button id="next" type="button" onClick={onContinue} style={S.btnPrimary}>
                    Continue
                </button>
                {error && (
                    <p id="error1" role="alert" style={S.error}>Name and email are required.</p>
                )}
            </fieldset>

            {step2 && (
                <fieldset id="step2" style={S.fieldset}>
                    <legend style={S.legend}>Step 2 — Confirm</legend>
                    <p id="summary" style={{ margin: '0 0 .5rem' }}>
                        {fullName} &lt;{email}&gt; — {plan} plan, {subscribe ? 'subscribed' : 'not subscribed'}.
                    </p>
                    <button data-testid="finish-btn" type="button" onClick={onFinish} style={S.btnPrimary}>
                        Finish
                    </button>
                </fieldset>
            )}

            {done && (
                <div id="done" style={S.done}>{done}</div>
            )}
        </form>
    );
}

const S = {
    form: { border: '1px dashed #c7d2fe', borderRadius: 10, padding: '1rem', background: '#fbfcff' },
    fieldset: { border: '1px solid #d0d7de', borderRadius: 8, marginBottom: '1rem', padding: '0.75rem 1rem' },
    legend: { fontSize: 13, fontWeight: 700, color: '#374151', padding: '0 6px' },
    label: { display: 'block', margin: '.5rem 0', fontSize: 13, color: '#374151' },
    checkRow: { display: 'flex', alignItems: 'center', gap: 8, margin: '.6rem 0', fontSize: 13, color: '#374151' },
    input: { display: 'block', width: '100%', maxWidth: 320, padding: '.4rem', marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6, boxSizing: 'border-box' },
    btnPrimary: { marginTop: 8, padding: '.45rem .9rem', background: '#2563eb', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontWeight: 600 },
    error: { color: '#b91c1c', fontSize: 13, marginTop: 8 },
    done: { color: '#0a7d33', fontWeight: 700, marginTop: 12, padding: '.6rem .8rem', background: '#ecfdf3', border: '1px solid #abefc6', borderRadius: 8 },
};
